/**
 * AccountRow — single row in the Account Settings table.
 *
 * Displays account alias, accountId, regions, enabled state, last updated,
 * and Edit/Delete action buttons.
 */
import type { MonitoredAccount } from '@/types';
import styles from './AccountRow.module.css';

export interface AccountRowProps {
  account: MonitoredAccount;
  onEdit: (account: MonitoredAccount) => void;
  onDelete: (accountId: number) => void;
  isDeletePending: boolean;
}

export function AccountRow({ account, onEdit, onDelete, isDeletePending }: AccountRowProps) {
  const lastUpdated = account.updatedAt
    ? new Date(account.updatedAt).toLocaleDateString()
    : 'Never';

  return (
    <tr className={styles.row}>
      <td className={styles.cell}>
        <div className={styles.nameCell}>
          <span className={styles.friendlyName}>{account.friendlyName}</span>
          <span
            className={`${styles.enabledBadge} ${account.enabled ? styles.enabled : styles.disabled}`}
            aria-label={account.enabled ? 'Enabled' : 'Disabled'}
          >
            {account.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </td>
      <td className={styles.cell}>
        <span className={styles.mono}>{account.accountId}</span>
      </td>
      <td className={styles.cell}>
        <div className={styles.regions}>
          {account.regions.map((r) => (
            <span key={r} className={styles.region}>
              {r}
            </span>
          ))}
        </div>
      </td>
      <td className={styles.cell}>
        <span className={styles.date}>{lastUpdated}</span>
      </td>
      <td className={styles.cell}>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.editButton}
            onClick={() => onEdit(account)}
            aria-label={`Edit account ${account.friendlyName}`}
          >
            Edit
          </button>
          <button
            type="button"
            className={styles.deleteButton}
            onClick={() => onDelete(account.id)}
            disabled={isDeletePending}
            aria-label={`Delete account ${account.friendlyName}`}
          >
            {isDeletePending ? '…' : 'Delete'}
          </button>
        </div>
      </td>
    </tr>
  );
}
